/* Checkout uses the installed size-specific Simple Bundles configurations. */
(()=>{if(customElements.get('tagtail-studio'))return;
customElements.define('tagtail-studio',class extends HTMLElement{
connectedCallback(){if(this.shadowRoot)return;
const host=this,root=this.attachShadow({mode:'open'});
const style=window.document.createElement('link');style.rel='stylesheet';style.href=host.dataset.cssUrl;root.append(style);
const template=host.querySelector('template');if(!template)return;root.append(template.content.cloneNode(true));
const document={getElementById:id=>root.getElementById(id),querySelector:s=>root.querySelector(s),querySelectorAll:s=>root.querySelectorAll(s),createElement:t=>window.document.createElement(t),addEventListener:(...args)=>root.addEventListener(...args),get activeElement(){return root.activeElement}};
// Deterministic product configurator: a prepared empty-cord photo layer plus
// the chosen original product photos. No image generation runs for customers.
function orderedPieces(design,catalog) {
  const lookup=new Map(catalog.charms.map(c=>[c.code,c]));
  const remaining={...design.charms},codes=[];
  for(const code of design.charmOrder||[])if(lookup.has(code)&&remaining[code]>0){codes.push(code);remaining[code]--;}
  for(const [code,n] of Object.entries(remaining))if(lookup.has(code))for(let i=0;i<n;i++)codes.push(code);
  return codes.map((code,index)=>({...lookup.get(code),instance:index}));
}

function syncCharmOrder(design,catalog) {
  design.charmOrder=orderedPieces(design,catalog).map(c=>c.code);
}

function layoutPieces(pieces,geometry={}) {
  const limit=12,visible=pieces.slice(0,limit);
  const centerX=geometry.centerX??.5,centerY=geometry.centerY??.69;
  const halfSpan=geometry.halfSpan??.225,rise=geometry.rise??.078;
  // Weighted slots retain a noticeable large/small difference while adapting
  // to the number of selected charms. Each instance is drawn separately.
  const weights=visible.map(c=>c.size==='small'?.64:1),weightSum=weights.reduce((a,b)=>a+b,0);
  const span=Math.min(halfSpan*2,.17*weightSum);
  let cursor=-weightSum/2;
  return {hidden:pieces.length-visible.length,total:pieces.length,items:visible.map((piece,index)=>{
    const weight=weights[index],fraction=weightSum>1?(cursor+weight/2)/(weightSum/2):0;
    cursor+=weight;
    const x=centerX+fraction*span/2;
    const normalized=(x-centerX)/halfSpan;
    const y=centerY-rise*normalized*normalized;
    const scale=Math.min(1,4/Math.max(4,weightSum));
    return {...piece,x,y,width:(piece.size==='small'?.105:.157)*scale,rotation:Math.atan(-2*rise*normalized/halfSpan)*.13};
  })};
}

function browserCanvas(width,height) {const c=document.createElement('canvas');c.width=width;c.height=height;return c;}
function browserImage(src) {return new Promise((resolve,reject)=>{const image=new Image();image.crossOrigin='anonymous';image.onload=()=>resolve(image);image.onerror=()=>reject(new Error('Product image unavailable'));image.src=src;});}

function createCompositor({makeCanvas=browserCanvas,loadImage=browserImage}={}) {
  const images=new Map(),cutouts=new Map(),tickets=new WeakMap();
  function getImage(src){if(!images.has(src))images.set(src,loadImage(src).catch(e=>{images.delete(src);throw e;}));return images.get(src);}

  async function getCutout(item) {
    const source=item.previewImage||item.image;
    if(cutouts.has(source))return cutouts.get(source);
    const task=(async()=>{
      const image=await getImage(source),w=image.naturalWidth||image.width,h=image.naturalHeight||image.height;
      if(item.previewImage)return {surface:image,x:0,y:0,w,h,anchorX:item.previewAnchorX??.5};
      const surface=makeCanvas(w,h),ctx=surface.getContext('2d',{willReadFrequently:true});ctx.drawImage(image,0,0,w,h);
      // A few catalogue photos use textured grey or black backgrounds.
      // Their silhouette masks preserve the actual photo's pale/dark details.
      if(item.previewMask) {
        const mask=makeCanvas(w,h),m=mask.getContext('2d');m.scale(w,h);m.fillStyle='#fff';
        for(const points of item.previewMask.polygons||[]){m.beginPath();points.forEach(([x,y],i)=>i?m.lineTo(x,y):m.moveTo(x,y));m.closePath();m.fill();}
        for(const e of item.previewMask.ellipses||[]){m.beginPath();m.ellipse(e[0],e[1],e[2],e[3],e[4]||0,0,Math.PI*2);m.fill();}
        m.globalCompositeOperation='destination-out';
        for(const e of item.previewMask.holes||[]){m.beginPath();m.ellipse(e[0],e[1],e[2],e[3],e[4]||0,0,Math.PI*2);m.fill();}
        ctx.globalCompositeOperation='destination-in';ctx.drawImage(mask,0,0);ctx.globalCompositeOperation='source-over';
      }
      const bitmap=ctx.getImageData(0,0,w,h),p=bitmap.data;
      // Clear only edge-connected studio background. Enclosed white details
      // and the original coloured acrylic artwork remain from the source.
      const corners=[[1,1],[w-2,1],[1,h-2],[w-2,h-2]];
      const channels=[0,1,2].map(ch=>corners.map(([x,y])=>p[(y*w+x)*4+ch]).sort((a,b)=>a-b));
      const bg=channels.map(c=>(c[1]+c[2])/2),brightness=(bg[0]+bg[1]+bg[2])/3;
      // Stay conservative around pale acrylic. A broad "white" threshold
      // would erase white gloves, bones and ice-cream tops with the backdrop.
      const threshold=item.previewBackgroundTolerance??(brightness>225?40:28);
      const visited=new Uint8Array(w*h),queue=new Int32Array(w*h);let head=0,tail=0;
      function matches(i){const j=i*4;if(p[j+3]===0)return true;const delta=Math.max(Math.abs(p[j]-bg[0]),Math.abs(p[j+1]-bg[1]),Math.abs(p[j+2]-bg[2]));return delta<threshold&&Math.max(p[j],p[j+1],p[j+2])-Math.min(p[j],p[j+1],p[j+2])<42;}
      function add(i){if(i>=0&&i<w*h&&!visited[i]&&matches(i)){visited[i]=1;queue[tail++]=i;}}
      if(!item.previewMask){for(let x=0;x<w;x++){add(x);add((h-1)*w+x);}for(let y=0;y<h;y++){add(y*w);add(y*w+w-1);}}
      while(head<tail){const i=queue[head++],x=i%w;p[i*4+3]=0;if(x)add(i-1);if(x<w-1)add(i+1);if(i>=w)add(i-w);if(i<w*(h-1))add(i+w);}
      let minX=w,minY=h,maxX=0,maxY=0;
      for(let y=0;y<h;y++)for(let x=0;x<w;x++)if(p[(y*w+x)*4+3]>0){minX=Math.min(minX,x);minY=Math.min(minY,y);maxX=Math.max(maxX,x);maxY=Math.max(maxY,y);}
      ctx.putImageData(bitmap,0,0);
      if(minX>=maxX||minY>=maxY)return {surface,x:0,y:0,w,h,anchorX:.5};
      // Locate the metal attachment near the top of the isolated silhouette.
      const topBand=Math.max(2,Math.round((maxY-minY)*.08));let sumX=0,samples=0;
      for(let y=minY;y<Math.min(h,minY+topBand);y++)for(let x=minX;x<=maxX;x++)if(p[(y*w+x)*4+3]>40){sumX+=x;samples++;}
      const anchorX=samples?(sumX/samples-minX)/(maxX-minX+1):.5;
      return {surface,x:minX,y:minY,w:maxX-minX+1,h:maxY-minY+1,anchorX:Math.max(.2,Math.min(.8,anchorX))};
    })().catch(e=>{cutouts.delete(source);throw e;});
    cutouts.set(source,task);return task;
  }

  async function render(canvas,design,catalog) {
    const ticket=(tickets.get(canvas)||0)+1;tickets.set(canvas,ticket);
    const cord=catalog.cords.find(c=>c.code===design.cord);if(!cord||(!cord.previewImage&&!catalog.preview?.sprite))throw new Error('Cord preview unavailable');
    const pieces=orderedPieces(design,catalog),layout=layoutPieces(pieces,cord.previewGeometry||catalog.preview.geometry);
    const sprite=await getImage(cord.previewImage||catalog.preview.sprite);
    if(tickets.get(canvas)!==ticket)return {stale:true};
    const w=1024,h=1024,temp=makeCanvas(w,h),ctx=temp.getContext('2d');ctx.fillStyle='#fff';ctx.fillRect(0,0,w,h);
    if(cord.previewImage){
      const frame=cord.previewFrame||{x:0,y:0,width:1,height:1};
      ctx.drawImage(sprite,frame.x*w,frame.y*h,frame.width*w,frame.height*h);
    }
    else {
      const cols=catalog.preview.columns,rows=catalog.preview.rows,index=cord.previewIndex;
      const sw=(sprite.naturalWidth||sprite.width)/cols,sh=(sprite.naturalHeight||sprite.height)/rows;
      ctx.drawImage(sprite,(index%cols)*sw,Math.floor(index/cols)*sh,sw,sh,0,0,w,h);
    }
    // Draw the empty cord first so a slow or failed charm cannot hide it.
    canvas.width=w;canvas.height=h;canvas.getContext('2d').drawImage(temp,0,0);
    const results=await Promise.allSettled(layout.items.map(c=>getCutout(c)));
    if(tickets.get(canvas)!==ticket)return {stale:true};
    const missing=[];
    layout.items.forEach((item,i)=>{
      if(results[i].status!=='fulfilled'){missing.push(item.code);return;}
      const part=results[i].value,drawW=item.width*w,drawH=drawW*part.h/part.w;
      ctx.save();ctx.translate(item.x*w,item.y*h);ctx.rotate(item.rotation);
      ctx.shadowColor='rgba(55,32,8,.13)';ctx.shadowBlur=5;ctx.shadowOffsetY=4;
      // Original jump-ring tip touches the bead strand instead of floating
      // below a thumbnail rectangle. There is no synthetic charm drawing.
      ctx.drawImage(part.surface,part.x,part.y,part.w,part.h,-drawW*part.anchorX,-drawH*.035,drawW,drawH);ctx.restore();
    });
    if(layout.hidden){ctx.fillStyle='#714517';ctx.font='26px Arial';ctx.textAlign='center';ctx.fillText(`+${layout.hidden} more selected · see the full list below`,w/2,h*.95);}
    // Keep the source limitation attached to enlarged and downloaded designs.
    if(cord.previewBasis==='catalogue-reconstruction'){
      ctx.fillStyle='#714517';ctx.font='22px Arial';ctx.textAlign='center';
      ctx.fillText('Catalogue-based illustration · cord details awaiting photo check',w/2,h*.985);
    }
    if(tickets.get(canvas)!==ticket)return {stale:true};
    canvas.width=w;canvas.height=h;canvas.getContext('2d').drawImage(temp,0,0);
    return {stale:false,layout,missing};
  }
  return {render,getCutout};
}



function purchaseItems(designs,catalog) {
  if(!designs.length)throw new Error('Add a necklace before continuing.');
  const result=[];
  for(const [index,d] of designs.entries()) {
    const size=SIZES[d.size],config=catalog.shopify?.bundles?.[d.size]||(d.size==='S'?catalog.shopify?.sBundle:null);
    if(!size||!config)throw new Error('Checkout is unavailable for this size. Your design is saved.');
    const problems=validateDesign(d,catalog);
    if(problems.length)throw new Error(problems.join(' '));
    const pieces=orderedPieces(d,catalog),large=pieces.filter(c=>c.size==='large'),small=pieces.filter(c=>c.size==='small');
    if(large.length<size.large||small.length<size.small)throw new Error(`Each ${d.size} necklace needs its ${size.large} included large and ${size.small} included small charms. Edit your design to complete the set.`);
    const cord=catalog.cords.find(c=>c.code===d.cord);
    const label=`${d.name||`Necklace ${index+1}`} [${d.id.slice(0,8)}]`;
    const signature=JSON.stringify([d.size,d.cord,d.charmOrder,d.spareCords,d.note,d.name]);
    const props={'Necklace':label,'Size':`${d.size} · ${size.range}`,'Charm order':pieces.map(c=>c.code).join(' → '),'_tagtail_design':d.id,'_tagtail_signature':signature};
    if(d.note)props['Note']=d.note;
    const selected={'Cord colour':cord.displayNameEn};
    for(const [label,items,limit] of [['Large',large,size.large],['Small',small,size.small]])for(let n=0;n<limit;n++)selected[`${label} charm ${n+1}`]=`${items[n].displayNameEn} · ${items[n].code}`;
    const selection=config.options.map(group=>group.map(option=>{
      const value=selected[option.optionName];
      if(!value||!option.optionValues.split(',').includes(value))throw new Error('This selection needs a catalogue update. Your design is saved; please contact ForPaws.');
      return value;
    }).join(' ++ ')).join(' <> ');
    result.push({id:Number(config.variantId),quantity:d.quantity,properties:{...props,...selected,'_bundle_selection':selection,'_tagtail_cord':d.cord,'_tagtail_charms':pieces.map(c=>c.code).join(',')},expectedPrice:size.base});
    const extras=new Map();
    for(const charm of [...large.slice(size.large),...small.slice(size.small)]) {
      const key=charm.shopifyVariantId;
      if(!key)throw new Error('A charm is not available for checkout.');
      const item=extras.get(key)||{id:Number(key.split('/').pop()),quantity:0,properties:{...props,'For necklace':label,'Extra charm':`${charm.displayNameEn} · ${charm.code}`},expectedPrice:PRICES[charm.size]};
      item.quantity+=d.quantity;extras.set(key,item);
    }
    result.push(...extras.values());
    if(d.spareCords)result.push({id:Number(cord.shopifyVariants[d.size].split('/').pop()),quantity:d.spareCords*d.quantity,properties:{...props,'For necklace':label,'Extra cord':`${cord.displayNameEn} · ${d.size}`},expectedPrice:1099});
  }
  if(result.length>100)throw new Error('Please check out with fewer different extra items at once. Your saved designs are kept.');
  return result;
}

const SIZES = Object.freeze({
  S: { range: '20–30 cm', base: 2499, large: 2, small: 2 },
  M: { range: '30–45 cm', base: 2899, large: 3, small: 2 },
  L: { range: '45–60 cm', base: 3299, large: 4, small: 3 }
});
const PRICES = Object.freeze({ large: 599, small: 399, cord: 1099 });
const MAX_QUANTITY = 99;
const money = cents => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);
const count = (value, fallback = 0, min = 0) => Number.isInteger(Number(value)) && Number(value) >= min && Number(value) <= MAX_QUANTITY ? Number(value) : fallback;
const id = () => globalThis.crypto?.randomUUID?.() || `design-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function emptyDesign(catalog) {
  return { id: id(), name: '', size: 'S', cord: catalog.cords[0].code, targets: {large:2,small:2}, charms: {}, charmOrder:[], quantity: 1, spareCords: 0, note: '' };
}

function normaliseDesign(raw, catalog) {
  if (!raw || !SIZES[raw.size] || !catalog.cords.some(c => c.code === raw.cord)) return null;
  const design = { id: typeof raw.id === 'string' && /^[a-zA-Z0-9-]{1,80}$/.test(raw.id) ? raw.id : id(), name: String(raw.name || '').slice(0,40), size: raw.size, cord: raw.cord, quantity: count(raw.quantity,1,1), spareCords:count(raw.spareCords), note:String(raw.note || '').slice(0,300), charms:{}, targets:{} };
  for (const item of catalog.charms) { const n=count(raw.charms?.[item.code]); if(n) design.charms[item.code]=n; }
  design.charmOrder=orderedPieces({...design,charmOrder:Array.isArray(raw.charmOrder)?raw.charmOrder.slice(0,MAX_QUANTITY*2):[]},catalog).map(c=>c.code);
  for(const type of ['large','small']) {
    const selected=selectedCount(design,catalog,type);
    if(selected > MAX_QUANTITY) return null;
    design.targets[type]=Math.max(count(raw.targets?.[type],SIZES[design.size][type]),selected);
  }
  return design;
}

function selectedCount(design, catalog, type) {
  return catalog.charms.filter(c => c.size===type).reduce((n,c)=>n+(design.charms[c.code] || 0),0);
}

function priceDesign(design) {
  const size=SIZES[design.size];
  const largeExtra=Math.max(0,design.targets.large-size.large);
  const smallExtra=Math.max(0,design.targets.small-size.small);
  const largeCost=largeExtra*PRICES.large, smallCost=smallExtra*PRICES.small, cordCost=design.spareCords*PRICES.cord;
  const unit=size.base+largeCost+smallCost+cordCost;
  return {base:size.base,largeExtra,smallExtra,largeCost,smallCost,cordCost,unit,total:unit*design.quantity,quantity:design.quantity};
}

function validateDesign(design,catalog) {
  if(!SIZES[design.size])return ['Choose a size.'];
  const issues=[];
  if(!catalog.cords.some(c=>c.code===design.cord))issues.push('Choose a cord colour.');
  for(const type of ['large','small']) {
    const selected=selectedCount(design,catalog,type), target=design.targets[type];
    if(selected!==target)issues.push(`Choose ${target-selected} more ${type} charm${target-selected===1?'':'s'}.`);
  }
  return issues;
}

function changeSize(design,nextSize,catalog) {
  if(!SIZES[nextSize])return;
  design.size=nextSize;
  for(const type of ['large','small']) design.targets[type]=Math.max(SIZES[nextSize][type],selectedCount(design,catalog,type));
}

function changeTarget(design,type,next,catalog) {
  if(!['large','small'].includes(type))return false;
  if(!Number.isInteger(next)||next<selectedCount(design,catalog,type)||next>MAX_QUANTITY||next<0)return false;
  design.targets[type]=next;return true;
}

function changeCharm(design,code,delta,catalog) {
  const charm=catalog.charms.find(c=>c.code===code);
  if(!charm||![1,-1].includes(delta))return false;
  const next=(design.charms[code]||0)+delta;
  if(next<0||next>MAX_QUANTITY)return false;
  if(delta>0&&selectedCount(design,catalog,charm.size)>=MAX_QUANTITY)return false;
  if(next)design.charms[code]=next;else delete design.charms[code];
  design.targets[charm.size]=Math.max(design.targets[charm.size],selectedCount(design,catalog,charm.size));
  return true;
}

function clearType(design,type,catalog) {
  for(const c of catalog.charms)if(c.size===type)delete design.charms[c.code];
}

function orderSnapshot(designs,catalog) {
  return { status:'draft-not-submitted', currency:'CAD', createdAt:new Date().toISOString(), subtotal:designs.reduce((s,d)=>s+priceDesign(d).total,0), necklaces:designs.map((d,index)=>{
    const cord=catalog.cords.find(c=>c.code===d.cord);
    return { designId:d.id, label:d.name || `Necklace ${index+1}`, size:d.size, neckRange:SIZES[d.size].range, cord:{name:cord.displayNameEn,code:cord.variants[d.size],previewBasis:cord.previewBasis||'cord-photo-render',detailsAwaitingPhotoCheck:cord.previewBasis==='catalogue-reconstruction'}, quantity:d.quantity, charmOrder:orderedPieces(d,catalog).map(c=>c.code), charms:catalog.charms.filter(c=>d.charms[c.code]).map(c=>({sku:c.code,name:c.displayNameEn,size:c.size,quantityPerNecklace:d.charms[c.code],totalQuantity:d.charms[c.code]*d.quantity})),included:{large:SIZES[d.size].large,small:SIZES[d.size].small},requested:{...d.targets},spareCordsPerNecklace:d.spareCords,price:priceDesign(d),note:d.note };
  }),pricingNote:'Charm prices are based on size: large $5.99, small $3.99. Extras apply after the included allowance for each size is used. Tax and delivery are excluded. This is a design list, not a placed order.' };
}

function orderText(designs,catalog) {
  const snap=orderSnapshot(designs,catalog);
  return ['FORPAWS × TAGTAIL — DESIGN LIST (NOT SUBMITTED)','Currency: CAD','',...snap.necklaces.flatMap((d,i)=>[
    `${i+1}. ${d.label} — ${d.quantity} necklace(s)`,
    `Design ID: ${d.designId}`,
    `Size ${d.size} | ${d.neckRange} | Cord: ${d.cord.name} (${d.cord.code})`,
    d.cord.detailsAwaitingPhotoCheck ? 'Cord preview: catalogue-based illustration; cord details await a new photo check.' : '',
    `Charms per necklace: ${d.charms.map(c=>`${c.name} [${c.sku}, ${c.size}] × ${c.quantityPerNecklace}`).join('; ') || 'None'}`,
    `Charm order (left to right in front-view preview): ${d.charmOrder.join(' → ') || 'None'}`,
    `Included: ${d.included.large} large + ${d.included.small} small`,
    `Base: ${money(d.price.base)} | Extra large: ${d.price.largeExtra} × ${money(PRICES.large)} | Extra small: ${d.price.smallExtra} × ${money(PRICES.small)}`,
    `Extra cords per necklace: ${d.spareCordsPerNecklace} × ${money(PRICES.cord)}`,
    `Each: ${money(d.price.unit)} | Line: ${money(d.price.total)}`,
    d.note ? `Note: ${d.note}` : '', ''
  ]),`Subtotal: CAD ${money(snap.subtotal)}`,snap.pricingNote].filter(x=>x!==undefined).join('\n');
}


const $=id=>document.getElementById(id);
const escape=value=>String(value).replace(/[&<>"']/g,ch=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
const STORAGE='forpaws-tagtail-shopify-preview-v1';
let catalog,design,bag=[],editing=null,previousDraft=null,filter='large',category='all',search='',toastTimer,storageOK=true;
const compositor=createCompositor();
let previewTicket=0;

function notify(message) {
  $('diyToast').textContent=message;
  clearTimeout(toastTimer);toastTimer=setTimeout(()=>$('diyToast').textContent='',4800);
}

function persist() {
  try {localStorage.setItem(STORAGE,JSON.stringify({design,bag,editing,previousDraft}));}
  catch {if(storageOK)notify('Your browser could not save this bag. Download the design list before leaving.');storageOK=false;}
}

function restore() {
  try {
    const raw=JSON.parse(localStorage.getItem(STORAGE)||'null');
    if(raw) {
      design=normaliseDesign(raw.design,catalog)||design;
      const ids=new Set();
      bag=(Array.isArray(raw.bag)?raw.bag:[]).map(d=>normaliseDesign(d,catalog)).filter(d=>d&&!validateDesign(d,catalog).length&&!ids.has(d.id)&&ids.add(d.id));
      editing=bag.some(d=>d.id===raw.editing)?raw.editing:null;
      previousDraft=normaliseDesign(raw.previousDraft,catalog);
    }
  } catch { /* A new draft is safe if stored data is corrupt or unavailable. */ }
}

function counter(action,key,value,min=0,label=key) {
  return `<div class="diy-counter"><button type="button" data-action="${action}" data-key="${key}" data-delta="-1" data-focus="${action}-${key}-minus" aria-label="Decrease ${escape(label)}" ${value<=min?'disabled':''}>−</button><input type="number" inputmode="numeric" min="${min}" max="${MAX_QUANTITY}" value="${value}" data-counter="${action}" data-key="${key}" data-focus="${action}-${key}-value" aria-label="${escape(label)}"><button type="button" data-action="${action}" data-key="${key}" data-delta="1" data-focus="${action}-${key}-plus" aria-label="Increase ${escape(label)}" ${value>=MAX_QUANTITY?'disabled':''}>+</button></div>`;
}

function syncFields() {
  $('diyPetName').value=design.name;$('diyNote').value=design.note;
}

function renderCords() {
  const cord=catalog.cords.find(c=>c.code===design.cord);
  $('diyCords').innerHTML=catalog.cords.map(c=>`<button class="diy-cord" type="button" data-cord="${c.code}" data-focus="cord-${c.code}" aria-label="Choose ${escape(c.displayNameEn)}${c.previewBasis==='catalogue-reconstruction'?' — reference render':''}" aria-pressed="${c.code===design.cord}" ${c.previewAvailable===false?'disabled':''}>${c.previewAvailable===false?'<span class="diy-cord-pending">New photo<br>coming soon</span>':`<img src="${c.previewImage||c.image}" alt="${escape(c.displayNameEn)} cord-only render, with fixed beads" width="140" height="140" loading="lazy">`}<span>${escape(c.displayNameEn)}</span>${c.previewBasis==='catalogue-reconstruction'?'<small class="diy-reference-badge">Reference render</small>':''}</button>`).join('');
  $('diyCordName').textContent=cord.displayNameEn;
  $('diyCordCaption').textContent=`Size ${design.size} · ${SIZES[design.size].range} · Cord ${cord.variants[design.size]}`;
  $('diyCordPhoto').disabled=!cord.originalImage;
  $('diyCordPhoto').textContent=cord.previewBasis==='catalogue-reconstruction'?'View catalogue reference ↗':'View cord photo ↗';
  $('diyCordPreviewNote').textContent=(cord.previewBasis==='catalogue-reconstruction'?'Reconstructed from a catalogue photo; cord details await a new photo check. ':'3D-style illustration based on a cord-only photo. ')+'Hanging charms appear only when selected. Proportions and bead layouts are illustrative; choose S / M / L by neck range.';
}

function renderTargets() {
  $('diyTargets').innerHTML=['large','small'].map(type=>{
    const n=selectedCount(design,catalog,type),limit=SIZES[design.size][type],target=design.targets[type];
    return `<div class="diy-target ${type}"><h4>${type==='large'?'Large':'Small'} charms</h4><p>${limit} included · ${money(PRICES[type])} per extra</p>${counter('target',type,target,0,`planned ${type} charms`)}<div class="diy-selected-count">${n} / ${target} chosen${n===target?' · Ready':''}</div><div class="diy-progress" aria-hidden="true"><span style="width:${target?Math.min(100,n/target*100):100}%"></span></div>${n?`<button type="button" class="diy-link" data-clear="${type}">Clear ${type} selection</button>`:''}</div>`;
  }).join('');
}

function renderSelected() {
  const pieces=orderedPieces(design,catalog),total=pieces.length;
  $('diyPieceCount').textContent=`${total} piece${total===1?'':'s'}`;
  $('diySelectedPieces').innerHTML=pieces.length?pieces.map((c,i)=>`<div class="diy-selected-piece ${c.size}"><button type="button" data-photo="${c.code}" aria-label="View ${escape(c.displayNameEn)}"><img src="${c.image}" alt="${escape(c.displayNameEn)}" loading="lazy"></button><b>${i+1}</b><small>${escape(c.displayNameEn)}</small><div class="diy-reorder"><button type="button" data-move="${i}" data-direction="-1" data-focus="move-${i}-left" aria-label="Move ${escape(c.displayNameEn)} left" ${i===0?'disabled':''}>←</button><button type="button" data-move="${i}" data-direction="1" data-focus="move-${i}-right" aria-label="Move ${escape(c.displayNameEn)} right" ${i===total-1?'disabled':''}>→</button></div></div>`).join(''):'<p class="diy-empty-pieces">Choose a charm to see it on the cord.</p>';
}

async function renderLivePreview() {
  const ticket=++previewTicket,canvas=$('diyLiveCanvas');
  $('diyLivePhoto').disabled=true;$('diySavePreview').disabled=true;
  $('diyLivePhoto').setAttribute('aria-busy','true');
  $('diyPreviewRetry').hidden=true;
  $('diyPreviewStatus').textContent='Updating your design…';
  try {
    const result=await compositor.render(canvas,design,catalog);
    if(result.stale||ticket!==previewTicket)return;
    const names=orderedPieces(design,catalog).map(c=>c.displayNameEn).join(', ');
    canvas.setAttribute('aria-label',`${$('diyCordName').textContent} with ${names||'no charms selected'}`);
    const missing=result.missing||[];
    $('diyPreviewStatus').textContent=missing.length?`Some photos could not load (${[...new Set(missing)].join(', ')}). Your selections are kept; tap Retry preview.`:result.layout.hidden?`Showing the first 12 of ${result.layout.total} charms. Every selected piece is kept in your design list.`:result.layout.total?`${result.layout.total} selected charm${result.layout.total===1?'':'s'} on your cord · updated just now`:'Cord + fixed beads only. Add a charm to see it appear here.';
    $('diyLivePhoto').disabled=false;$('diySavePreview').disabled=missing.length>0;
    $('diyPreviewRetry').hidden=missing.length===0;
  } catch {
    if(ticket!==previewTicket)return;
    canvas.getContext('2d').clearRect(0,0,canvas.width,canvas.height);
    const cord=catalog.cords.find(c=>c.code===design.cord);
    $('diyPreviewStatus').textContent=cord.previewAvailable===false?'An updated photo for this colour is coming soon. Your saved choices are kept; choose one of the eight photographed colours to preview.':'Preview could not load. Your choices are saved. Tap Retry preview or view the original photo.';
    $('diyPreviewRetry').hidden=cord.previewAvailable===false;
  } finally {if(ticket===previewTicket)$('diyLivePhoto').setAttribute('aria-busy','false');}
}

function openLivePreview() {
  $('diyPhotoTitle').textContent=`Your design · ${$('diyCordName').textContent}`;
  $('diyZoomImage').src=$('diyLiveCanvas').toDataURL('image/png');
  $('diyZoomImage').alt=$('diyLiveCanvas').getAttribute('aria-label');
  $('diyZoomCaption').textContent=$('diyCordPreviewNote').textContent+' Your chosen charm order is saved with this necklace.';
  $('diyPhotoDialog').showModal();
}

function row(label,value,extra='') {return `<div class="diy-money-row ${extra}"><span>${label}</span><strong>${value}</strong></div>`;}
function renderPrices() {
  const p=priceDesign(design),s=SIZES[design.size];
  $('diyLivePrice').textContent=`CAD ${money(p.unit)} / necklace`;
  $('diyPriceBreakdown').innerHTML=row(`Size ${design.size} · ${s.large} large + ${s.small} small`,money(p.base))+row(`Extra large · ${p.largeExtra} × ${money(PRICES.large)}`,money(p.largeCost))+row(`Extra small · ${p.smallExtra} × ${money(PRICES.small)}`,money(p.smallCost))+(design.spareCords?row(`Extra cords · ${design.spareCords} × ${money(PRICES.cord)}`,money(p.cordCost)):'')+row('Each necklace',money(p.unit),'total')+(design.quantity>1?row(`${design.quantity} matching necklaces`,money(p.total)):'');
  $('diySpareCounter').innerHTML=counter('spare','cords',design.spareCords,0,'extra cords per necklace');
  $('diyCopyCounter').innerHTML=counter('copies','necklaces',design.quantity,1,'matching necklace quantity');
  $('diyAdd').textContent=`${editing?'Save necklace changes':'Add to bag'} · CAD ${money(p.total)}`;
  const issues=validateDesign(design,catalog);
  $('diyValidation').textContent=issues.length?issues.join(' '):`Ready: ${design.targets.large} large + ${design.targets.small} small per necklace.`;
}

function renderCatalog() {
  document.querySelectorAll('#diyTypes [data-type]').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.type===filter)));
  const q=search.trim().toLowerCase();
  const visible=catalog.charms.filter(c=>(filter==='all'||filter===c.size||(filter==='selected'&&design.charms[c.code]))&&(category==='all'||c.categoryEn===category)&&(!q||`${c.displayNameEn} ${c.code} ${c.name}`.toLowerCase().includes(q)));
  $('diyResults').textContent=`${visible.length} original product photo${visible.length===1?'':'s'}`;
  $('diyCatalog').innerHTML=visible.length?visible.map(c=>{
    const n=design.charms[c.code]||0,included=selectedCount(design,catalog,c.size)<SIZES[design.size][c.size];
    const nextPrice=included?'Next piece included':`Next piece +${money(PRICES[c.size])}`;
    return `<article class="diy-card ${n?'is-selected':''}"><button type="button" class="diy-card-photo" data-photo="${c.code}" aria-label="Enlarge ${escape(c.displayNameEn)} original photo"><img src="${c.image}" alt="${escape(c.displayNameEn)}" loading="lazy" width="160" height="160"><span class="diy-magnify" aria-hidden="true">↗</span></button><div class="diy-card-info"><h4>${escape(c.displayNameEn)}</h4><p class="diy-card-meta">${c.size==='large'?'Large':'Small'} · ${c.code}</p><p class="diy-card-price">${nextPrice}</p>${counter('charm',c.code,n,0,`${c.displayNameEn} quantity`)}</div></article>`;
  }).join(''):'<p class="diy-none">No matching pieces. Try another series or search.</p>';
}

function refresh({catalogChanged=true}={}) {
  const focus=document.activeElement?.dataset?.focus;
  syncCharmOrder(design,catalog);
  document.querySelectorAll('#diySizes button').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.size===design.size)));
  renderCords();renderTargets();renderSelected();renderPrices();renderLivePreview();
  if(catalogChanged)renderCatalog();
  persist();
  if(focus){const el=document.querySelector(`[data-focus="${CSS.escape(focus)}"]`);if(el&&!el.disabled)el.focus({preventScroll:true});}
}

function renderBag() {
  const total=bag.reduce((sum,d)=>sum+priceDesign(d).total,0),quantity=bag.reduce((sum,d)=>sum+d.quantity,0);
  $('diyBagCount').textContent=quantity;
  $('diyBagItems').innerHTML=bag.length?bag.map((d,index)=>{
    const c=catalog.cords.find(c=>c.code===d.cord),p=priceDesign(d);
    const pieces=catalog.charms.filter(c=>d.charms[c.code]);
    return `<article class="diy-bag-card"><div><canvas class="diy-bag-photo" data-bag-preview="${index}" width="960" height="1200" role="img" aria-label="${escape(d.name||`Necklace ${index+1}`)} styling preview"></canvas><small class="diy-fine">${c.previewBasis==='catalogue-reconstruction'?'Reference render · awaiting photo check':'Your design'}</small></div><div><h3>${escape(d.name||`Necklace ${index+1}`)}</h3><p>Size ${d.size} · ${SIZES[d.size].range} · ${escape(c.displayNameEn)}</p><p>${d.targets.large} large + ${d.targets.small} small per necklace${d.spareCords?` · ${d.spareCords} extra cord${d.spareCords===1?'':'s'}`:''}</p><div class="diy-bag-thumbs">${pieces.map(c=>`<span title="${escape(c.displayNameEn)}"><img src="${c.image}" alt="${escape(c.displayNameEn)}" loading="lazy">×${d.charms[c.code]}</span>`).join('')}</div><p>Each ${money(p.unit)} · base ${money(p.base)} + extras ${money(p.unit-p.base)}</p><div class="diy-bag-actions">${counter('bag',d.id,d.quantity,1,`quantity for ${d.name||'this necklace'}`)}<strong class="diy-bag-line-total">${money(p.total)}</strong></div><div class="diy-bag-actions"><button class="diy-link" type="button" data-bag-edit="${d.id}">Edit</button><button class="diy-link" type="button" data-bag-copy="${d.id}">Copy & customise</button><button class="diy-link" type="button" data-bag-remove="${d.id}">Remove</button></div></div></article>`;
  }).join(''):'<div class="diy-bag-empty"><strong>Your first design starts above.</strong><p>Add a necklace, then make another for a different pet or occasion.</p></div>';
  $('diyBagTotals').innerHTML=row('Different designs',bag.length)+row('Necklaces',quantity)+row('Estimated subtotal',`CAD ${money(total)}`,'total')+'<p class="diy-fine">Tax and delivery are not included. Each necklace keeps its own charm allowance.</p>';
  $('diyReview').disabled=!bag.length;
  $('diyReview').style.opacity=bag.length?'1':'.45';
  document.querySelectorAll('[data-bag-preview]').forEach(canvas=>{
    compositor.render(canvas,bag[Number(canvas.dataset.bagPreview)],catalog).catch(()=>{canvas.parentElement.querySelector('small').textContent='Preview unavailable';});
  });
  persist();
}

function openPhoto(code,cordMode=false) {
  const item=cordMode?catalog.cords.find(c=>c.code===code):catalog.charms.find(c=>c.code===code);
  if(!item)return;
  $('diyPhotoTitle').textContent=item.displayNameEn;
  $('diyZoomImage').src=cordMode?item.originalImage:item.image;$('diyZoomImage').alt=item.displayNameEn;
  $('diyZoomCaption').textContent=cordMode?`${item.name} · ${item.previewBasis==='catalogue-reconstruction'?'Catalogue reference: the hanging charms in this source photo are examples, not automatically included in your selection. The reconstructed cord preview awaits a new photo check.':'Cord-only source photo. Fixed threaded beads are part of the cord; hanging charms are chosen separately.'} Photo framing does not compare sizes or confirm fixed bead counts across sizes. Your chosen size is ${design.size} (${SIZES[design.size].range}).`:`${item.name} · ${item.code} · ${item.size==='large'?'Large':'Small'}${item.dimensions?` · ${item.dimensions.replaceAll('*',' × ')}`:''}. Original image from the product catalogue. Reverse side is not shown.`;
  $('diyPhotoDialog').showModal();
}

function applyCounter(action,key,value) {
  if(!Number.isInteger(value)||value<0||value>MAX_QUANTITY){notify('Please enter a whole number from 0 to 99.');if(action==='bag')renderBag();refresh();return;}
  if(action==='target') {
    if(!changeTarget(design,key,value,catalog))notify(`Remove some selected ${key} charms before lowering the total.`);
  } else if(action==='charm') {
    const c=catalog.charms.find(c=>c.code===key);if(!c)return;
    const old=design.charms[key]||0,newTotal=selectedCount(design,catalog,c.size)-old+value;
    if(newTotal>MAX_QUANTITY){notify(`Choose up to ${MAX_QUANTITY} ${c.size} pieces per necklace.`);refresh();return;}
    if(value)design.charms[key]=value;else delete design.charms[key];
    if(newTotal>design.targets[c.size]){design.targets[c.size]=newTotal;notify(`${c.size==='large'?'Large':'Small'} charm count increased to ${newTotal}. Price updated.`);}
  } else if(action==='copies'){if(value<1){refresh();return;}design.quantity=value;}
  else if(action==='spare')design.spareCords=value;
  else if(action==='bag') {
    const d=bag.find(d=>d.id===key);if(!d||value<1){renderBag();return;}d.quantity=value;
    if(editing===key)design.quantity=value;
    const focus=document.activeElement?.dataset?.focus;renderBag();if(focus)document.querySelector(`[data-focus="${CSS.escape(focus)}"]`)?.focus({preventScroll:true});refresh({catalogChanged:false});return;
  }
  refresh();
}

function currentCounter(action,key) {
  if(action==='target')return design.targets[key];
  if(action==='charm')return design.charms[key]||0;
  if(action==='copies')return design.quantity;
  if(action==='spare')return design.spareCords;
  return bag.find(d=>d.id===key)?.quantity||1;
}

function draftSignature(d) {
  return JSON.stringify([d.name,d.size,d.cord,d.targets.large,d.targets.small,Object.entries(d.charms).sort(([a],[b])=>a.localeCompare(b)),d.charmOrder,d.quantity,d.spareCords,d.note]);
}

function saveDesignToBag() {
  const issues=validateDesign(design,catalog);
  if(issues.length){notify(issues.join(' '));filter=selectedCount(design,catalog,'large')<design.targets.large?'large':'small';category='all';search='';$('diyCategory').value='all';$('diySearch').value='';renderCatalog();$('diyCharmHeading').scrollIntoView({behavior:'smooth',block:'center'});return false;}
  if(editing){const index=bag.findIndex(d=>d.id===editing);if(index>=0)bag[index]=structuredClone(design);else bag.push(structuredClone(design));}
  else bag.push(structuredClone({...design,id:id()}));
  notify(editing?'Necklace updated.':'Necklace added. You can now design another.');
  design=previousDraft||emptyDesign(catalog);editing=null;previousDraft=null;
  syncFields();refresh();renderBag();return true;
}

function openReview(savedOnly=false) {
  if(!bag.length)return;
  if(editing&&!saveDesignToBag())return;
  const unsaved=!savedOnly&&draftSignature(design)!==draftSignature(emptyDesign(catalog));
  $('diyReviewPrompt').hidden=!unsaved;
  $('diyReviewDetails').hidden=unsaved;
  $('diyOrderFeedback').textContent='';
  if(unsaved){$('diyOrderDialog').showModal();return;}
  const snapshot=orderSnapshot(bag,catalog);
  $('diyOrderContent').innerHTML=snapshot.necklaces.map(d=>`<article class="diy-review-item"><h4>${escape(d.label)} × ${d.quantity}</h4><p>Size ${d.size} · ${d.neckRange} · ${escape(d.cord.name)} · Cord ${d.cord.code}</p><p>Left → right: ${d.charmOrder.map(code=>escape(catalog.charms.find(c=>c.code===code)?.displayNameEn||code)).join(" → ")||"No charms"}</p><p>Pieces per necklace:</p><ul>${d.charms.map(c=>`<li>${escape(c.name)} · ${c.sku} · ${c.size} × ${c.quantityPerNecklace}</li>`).join('')||'<li>No charms requested</li>'}</ul><p>Base ${money(d.price.base)} + ${d.price.largeExtra} extra large (${money(d.price.largeCost)}) + ${d.price.smallExtra} extra small (${money(d.price.smallCost)})${d.spareCordsPerNecklace?` + extra cords (${money(d.price.cordCost)})`:''}</p><p><strong>Each ${money(d.price.unit)} · ${d.quantity} ${d.quantity===1?'necklace':'necklaces'} · CAD ${money(d.price.total)}</strong></p>${d.note?`<p>Note: ${escape(d.note)}</p>`:''}</article>`).join('')+row('Estimated subtotal',`CAD ${money(snapshot.subtotal)}`,'total')+'<p class="diy-fine">Charms are priced by size: large $5.99, small $3.99. Tax and delivery are excluded.</p>';
  $('diyCheckout').hidden=false;
  try { purchaseItems(bag,catalog);$('diyCheckout').disabled=false;$('diyOrderStatus').textContent='Your necklaces are ready to add to your Shopify cart. Extras are separate priced items linked to each necklace. Final stock, tax and delivery are checked by Shopify.'; }
  catch(error) { $('diyCheckout').disabled=true;$('diyOrderStatus').textContent=error.message; }
  $('diyOrderFeedback').textContent='';$('diyOrderDialog').showModal();
}

function beginEditing(source,duplicate=false) {
  if(!editing)previousDraft=structuredClone(design);
  design=structuredClone(source);
  if(duplicate){design.id=id();design.name=`${source.name||'Necklace'} · copy`.slice(0,40);editing=null;}
  else editing=source.id;
  syncFields();refresh();$('create').scrollIntoView({behavior:'smooth'});
  notify(duplicate?'A copy is ready to customise. Add it as a separate design.':'Editing this necklace. Save changes when you are ready.');
}


let checkoutBusy=false;
const shopRoot=window.Shopify?.routes?.root||'/';
async function shopJson(path,options={}) {
  const response=await fetch(shopRoot+path,{credentials:'same-origin',...options});
  const data=await response.json();
  if(!response.ok)throw new Error(data.description||data.message||'Shopify could not update the cart. Please try again.');
  return data;
}
async function checkoutSavedDesigns() {
  if(checkoutBusy)return;
  checkoutBusy=true;$('diyCheckout').disabled=true;
  $('diyOrderFeedback').textContent='Checking your choices and current prices…';
  try {
    const planned=purchaseItems(bag,catalog);
    const products=await Promise.all([...new Set(bag.map(d=>(catalog.shopify.bundles?.[d.size]||catalog.shopify.sBundle).handle)),'tagtail-beaded-cords','tagtail-large-charms','tagtail-small-charms'].map(h=>shopJson(`products/${h}.js`)));
    const variants=new Map(products.flatMap(p=>p.variants).map(v=>[Number(v.id),v]));
    for(const d of bag) {
      const cord=catalog.cords.find(c=>c.code===d.cord);
      const selected=[{id:cord.shopifyVariants[d.size],name:cord.displayNameEn},...catalog.charms.filter(c=>d.charms[c.code]).map(c=>({id:c.shopifyVariantId,name:c.displayNameEn}))];
      for(const item of selected)if(!variants.get(Number(item.id.split('/').pop()))?.available)throw new Error(`${item.name} is unavailable. Please edit your necklace and choose another item.`);
    }
    for(const item of planned) {
      const v=variants.get(item.id);
      if(!v?.available)throw new Error('One of your selected items is unavailable. Your designs are saved; choose another item.');
      if(v.price!==item.expectedPrice)throw new Error('A product price has changed. Please contact ForPaws before ordering this design.');
    }
    const cart=await shopJson('cart.js');
    if(cart.currency!=='CAD')throw new Error('Please select Canada / CAD in the store before continuing.');
    const missing=[];
    for(const item of planned) {
      const existing=cart.items.filter(i=>Number(i.variant_id)===item.id&&i.properties?._tagtail_design===item.properties._tagtail_design&&i.properties?._tagtail_signature===item.properties._tagtail_signature);
      if(existing.length) {
        if(existing.reduce((n,i)=>n+i.quantity,0)!==item.quantity)throw new Error('This design is already in your Shopify cart with a different quantity. Review that cart before adding it again.');
      } else missing.push(item);
    }
    if(missing.length) {
      $('diyOrderFeedback').textContent='Adding your necklaces and extras to the cart…';
      await shopJson('cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:missing.map(({expectedPrice,...item})=>item)})});
    }
    const verified=await shopJson('cart.js');
    for(const item of planned) {
      const found=verified.items.filter(i=>Number(i.variant_id)===item.id&&i.properties?._tagtail_design===item.properties._tagtail_design&&i.properties?._tagtail_signature===item.properties._tagtail_signature);
      if(found.reduce((n,i)=>n+i.quantity,0)!==item.quantity)throw new Error('Please review your Shopify cart: some quantities could not be confirmed. Your designs are saved.');
    }
    $('diyOrderFeedback').textContent='Your designs are in the cart. Opening your order summary…';
    window.location.assign(shopRoot+'cart');
  } catch(error) {
    $('diyOrderFeedback').textContent=error.message+' No payment has been taken. You can review your Shopify cart before retrying.';
  } finally {checkoutBusy=false;$('diyCheckout').disabled=false;}
}

function initEvents() {
  $('diyCheckout').addEventListener('click',checkoutSavedDesigns);
  $('diySizes').addEventListener('click',e=>{const b=e.target.closest('[data-size]');if(!b)return;changeSize(design,b.dataset.size,catalog);refresh();});
  $('diyCords').addEventListener('click',e=>{const b=e.target.closest('[data-cord]');if(!b)return;design.cord=b.dataset.cord;refresh({catalogChanged:false});});
  $('diyTypes').addEventListener('click',e=>{const b=e.target.closest('[data-type]');if(!b)return;filter=b.dataset.type;renderCatalog();});
  $('diySearch').addEventListener('input',e=>{search=e.target.value;renderCatalog();});
  $('diyCategory').addEventListener('change',e=>{category=e.target.value;renderCatalog();});
  $('diyShowSelected').addEventListener('click',()=>{filter='selected';category='all';search='';$('diySearch').value='';$('diyCategory').value='all';renderCatalog();});
  $('diyPetName').addEventListener('input',e=>{design.name=e.target.value;persist();});
  $('diyNote').addEventListener('input',e=>{design.note=e.target.value;persist();});
  $('diyCordPhoto').addEventListener('click',()=>openPhoto(design.cord,true));
  $('diyPreviewRetry').addEventListener('click',renderLivePreview);
  $('diyViewDesign').addEventListener('click',()=>$('diyLivePhoto').scrollIntoView({behavior:'smooth',block:'center'}));
  $('diyExample').addEventListener('click',()=>{
    if(!editing)previousDraft=structuredClone(design);
    design={...emptyDesign(catalog),name:'Sunshine Red · ABC',cord:'TTS017',targets:{large:3,small:0},charms:{TTB077:1,TTB078:1,TTB079:1},charmOrder:['TTB077','TTB078','TTB079']};
    editing=null;syncFields();refresh();
    $('diyLivePhoto').scrollIntoView({behavior:'smooth',block:'center'});
    notify('ABC example loaded. Change the colour, add more pieces, or rearrange the letters.');
  });
  $('diyLivePhoto').addEventListener('click',openLivePreview);
  $('diySavePreview').addEventListener('click',()=>{
    const source=$('diyLiveCanvas'),canvas=document.createElement('canvas');canvas.width=source.width;canvas.height=source.height+100;
    const ctx=canvas.getContext('2d');ctx.fillStyle='#fff';ctx.fillRect(0,0,canvas.width,canvas.height);ctx.drawImage(source,0,0);
    ctx.fillStyle='#75400e';ctx.font='bold 27px Arial';ctx.fillText(`TAGTAIL · ${$('diyCordName').textContent} · Size ${design.size}`,30,source.height+35);
    ctx.font='21px Arial';ctx.fillText('Styling preview · original charm photos · scale and cord details may vary',30,source.height+74);
    const a=document.createElement('a');a.href=canvas.toDataURL('image/png');a.download=`Tagtail-${design.cord}-design.png`;a.click();
  });
  document.addEventListener('click',e=>{
    const move=e.target.closest('[data-move]');
    if(move){const i=Number(move.dataset.move),to=i+Number(move.dataset.direction);if(to>=0&&to<design.charmOrder.length){[design.charmOrder[i],design.charmOrder[to]]=[design.charmOrder[to],design.charmOrder[i]];refresh({catalogChanged:false});document.querySelector(`[data-move="${to}"][data-direction="${move.dataset.direction}"]:not(:disabled)`)?.focus({preventScroll:true});}return;}
    const action=e.target.closest('[data-action]');
    if(action){const {action:a,key,delta}=action.dataset;applyCounter(a,key,currentCounter(a,key)+Number(delta));return;}
    const photo=e.target.closest('[data-photo]');if(photo){openPhoto(photo.dataset.photo);return;}
    const close=e.target.closest('[data-close]');if(close){$(close.dataset.close).close();return;}
    const clear=e.target.closest('[data-clear]');if(clear){clearType(design,clear.dataset.clear,catalog);refresh();return;}
    const edit=e.target.closest('[data-bag-edit]');if(edit){beginEditing(bag.find(d=>d.id===edit.dataset.bagEdit));return;}
    const copy=e.target.closest('[data-bag-copy]');if(copy){beginEditing(bag.find(d=>d.id===copy.dataset.bagCopy),true);return;}
    const remove=e.target.closest('[data-bag-remove]');if(remove){bag=bag.filter(d=>d.id!==remove.dataset.bagRemove);if(editing===remove.dataset.bagRemove){editing=null;design=previousDraft||emptyDesign(catalog);previousDraft=null;syncFields();refresh();}renderBag();notify('Necklace removed from your bag.');}
  });
  document.addEventListener('change',e=>{if(e.target.matches('[data-counter]'))applyCounter(e.target.dataset.counter,e.target.dataset.key,Number(e.target.value));});
  $('diyAdd').addEventListener('click',()=>{if(saveDesignToBag())$('bag').scrollIntoView({behavior:'smooth'});});
  $('diyUseSelected').addEventListener('click',()=>{for(const type of ['large','small'])design.targets[type]=selectedCount(design,catalog,type);refresh();notify('Using only your selected charms. The package base price stays the same.');});
  $('diyReviewAddDraft').addEventListener('click',()=>{$('diyOrderDialog').close();if(saveDesignToBag())openReview(true);});
  $('diyReviewSaved').addEventListener('click',()=>openReview(true));
  document.querySelectorAll('[data-diy-step]').forEach(button=>button.addEventListener('click',()=>{$(button.dataset.diyStep).scrollIntoView({behavior:'smooth',block:'start'});}));
  $('diyNew').addEventListener('click',()=>{
    if(editing){editing=null;design=previousDraft||emptyDesign(catalog);previousDraft=null;syncFields();refresh();}
    $('create').scrollIntoView({behavior:'smooth'});
  });
  $('diyReview').addEventListener('click',()=>openReview());
  for(const dialog of [$('diyPhotoDialog'),$('diyOrderDialog')])dialog.addEventListener('click',e=>{if(e.target===dialog){const r=dialog.getBoundingClientRect();if(e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom)dialog.close();}});
  $('diyCopyOrder').addEventListener('click',async()=>{
    try {await navigator.clipboard.writeText(orderText(bag,catalog));$('diyOrderFeedback').textContent='Design list copied. It has not been sent or ordered.';}
    catch {$('diyOrderFeedback').textContent='Copy is unavailable in this browser. Download your design list instead.';}
  });
  $('diyDownloadOrder').addEventListener('click',()=>{
    const blob=new Blob([orderText(bag,catalog)],{type:'text/plain;charset=utf-8'}),url=URL.createObjectURL(blob),a=document.createElement('a');a.href=url;a.download='forpaws-tagtail-design-list.txt';a.click();setTimeout(()=>URL.revokeObjectURL(url),1000);$('diyOrderFeedback').textContent='Design list downloaded. No order has been submitted.';
  });
}

async function init() {
  const response=await fetch(host.dataset.catalogUrl);if(!response.ok)throw new Error('Catalogue unavailable');
  catalog=await response.json();
  if(!catalog.cords?.length||!catalog.charms?.length)throw new Error('Catalogue incomplete');
  design=emptyDesign(catalog);restore();
  const categories=[...new Set(catalog.charms.map(c=>c.categoryEn))];
  $('diyCategory').innerHTML='<option value="all">Every series</option>'+categories.map(c=>`<option value="${escape(c)}">${escape(c)}</option>`).join('');
  initEvents();syncFields();refresh();renderBag();
  $('diyLoading').hidden=true;$('diyWorkspace').hidden=false;
  $('diyExample').disabled=false;
  // Shopify performs final price and stock checks for the selected size bundle.

}

init().catch(()=>{
  $('diyLoading').hidden=false;$('diyLoading').textContent='The collection could not load. Refresh this page to try again; your saved bag has not been removed.';
  $('diyReview').disabled=true;$('diyNew').disabled=true;
});

}});})();
